import { useCallback, useMemo } from 'react'
import omit from 'lodash.omit'
import ReactSelect, {
    ClearIndicatorProps,
    components,
    Props as ReactSelectProps,
    MultiValueProps,
    GroupBase,
    ControlProps
} from 'react-select'
import AsyncReactSelect, {
    AsyncProps as AsyncReactSelectProps
} from 'react-select/async'
import {
    useTiaInput,
    useTiaUI,
    TiaCoreInputProps,
    Whisper,
    Box,
    ThemeStyleObject,
    Icon
} from '../'
import { css as getCss, Theme, CSSObject, CSSProperties } from 'theme-ui'
import { Controller } from 'react-hook-form'

const MultiValueRemove = (props: MultiValueProps) => {
    return (
        <components.MultiValueRemove {...props}>
            <Box
                css={{
                    width: 15,
                    height: 15,
                    marginLeft: -4,
                    cursor: 'pointer'
                }}
            >
                <Icon.Close color="text" sx={{ fontSize: 15 }} />
            </Box>
        </components.MultiValueRemove>
    )
}

const ClearIndicator = (props: ClearIndicatorProps) => {
    const {
        getStyles,
        innerProps: { ref, ...restInnerProps }
    } = props
    const styles = getStyles('clearIndicator', props) as CSSProperties

    return (
        <Box
            {...restInnerProps}
            // @ts-expect-error need to upgrade theme-ui
            ref={ref}
            sx={{
                ...styles,
                'fontSize': 4,
                'padding': 2,
                'cursor': 'pointer',
                '&:hover > svg > path': {
                    fill: styles[':hover']?.color
                }
            }}
        >
            <Icon.Close color={styles.color} />
        </Box>
    )
}

const MagnifyingGlassIcon = (props: { sx?: ThemeStyleObject }) => (
    <Box
        as="svg"
        sx={{
            width: 24,
            height: 24,
            ...props.sx
        }}
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.9907 13.795H15.7321L19.7114 17.7931C20.0962 18.1779 20.0962 18.8067 19.7114 19.1915C19.3266 19.5763 18.6978 19.5763 18.313 19.1915L14.3243 15.2028V14.4614L14.0709 14.1986C12.757 15.3248 10.9644 15.9067 9.05923 15.5876C6.45015 15.1465 4.36663 12.9691 4.04754 10.3413C3.55951 6.37132 6.90064 3.03019 10.8706 3.51822C13.4984 3.83731 15.6758 5.92083 16.1169 8.52991C16.436 10.4351 15.8541 12.2277 14.7279 13.5416L14.9907 13.795ZM5.87893 9.57203C5.87893 11.9089 7.76535 13.7954 10.1023 13.7954C12.4392 13.7954 14.3256 11.9089 14.3256 9.57203C14.3256 7.23512 12.4392 5.34869 10.1023 5.34869C7.76535 5.34869 5.87893 7.23512 5.87893 9.57203Z"
            fill="#765454"
        />
    </Box>
)

// Renaming react-select prop "isLoading" to "loadingOptions" since TiaInput has a "loading" prop with different semantic
export type SelectProps<TOption = unknown> = Omit<
    ReactSelectProps<TOption>,
    'isLoading'
> &
    TiaCoreInputProps & { loadingOptions?: boolean; sx?: ThemeStyleObject }

export type AsyncSelectProps<TOption = unknown> = Omit<
    AsyncReactSelectProps<TOption, boolean, GroupBase<TOption>>,
    'isLoading'
> &
    TiaCoreInputProps & { sx?: ThemeStyleObject }

const useSelectStyles = (
    theme: Theme,
    borderColor: string = 'inputOutline'
) => {
    const css = useCallback(
        (o: ThemeStyleObject): CSSObject => getCss(o)(theme),
        [theme]
    )
    // borderColor = 'inputOutline'
    const styles = useMemo(
        () => ({
            container: (styles: CSSObject) => ({
                ...styles,
                ...css({
                    fontFamily: 'body',
                    fontSize: 4,
                    borderColor: 'inputOutline',
                    mb: 5 // adding the same margin as the rest of the input components
                })
            }),
            control: (
                styles: CSSObject,
                {
                    isFocused,
                    isDisabled
                }: { isFocused: boolean; isDisabled: boolean }
            ) => {
                let backgroundColor = 'inputBackground'
                if (isDisabled) {
                    backgroundColor = 'backgroundDisabled'
                } else if (isFocused) {
                    backgroundColor = 'inputBackground'
                }

                return {
                    ...styles,
                    boxShadow: 'none',
                    ...css({
                        'borderRadius': 2,
                        backgroundColor,
                        borderColor,
                        'borderWidth': 1,
                        ':hover': {
                            borderRadius: 2,
                            backgroundColor,
                            borderColor: 'inputOutlineHover',
                            borderWidth: 1
                        }
                    })
                }
            },
            placeholder: (
                styles: CSSObject,
                { isDisabled }: { isDisabled: boolean }
            ) => ({
                ...styles,
                ...css({
                    color: isDisabled ? 'placeholderDisabled' : 'placeholder'
                })
            }),
            valueContainer: (styles: CSSObject) => ({
                ...styles,
                minHeight: 46,
                padding: '5px 12px'
            }),
            option: (
                styles: CSSObject,
                {
                    isDisabled,
                    isFocused,
                    isSelected
                }: {
                    isDisabled: boolean
                    isFocused: boolean
                    isSelected: boolean
                }
            ): CSSObject => {
                let backgroundColor = 'inputBackground'

                if (!isDisabled && isSelected) {
                    backgroundColor = 'inputBackgroundSelected'
                } else if (!isDisabled && isFocused) {
                    backgroundColor = 'inputBackgroundSelected'
                }

                return {
                    ...styles,
                    ...css({
                        backgroundColor,
                        color: 'text'
                    })
                }
            },
            singleValue: (styles: CSSObject) => ({
                ...styles,
                ...css({
                    color: 'text'
                })
            }),
            multiValue: (styles: CSSObject) => ({
                ...styles,
                marginTop: 2,
                marginBottom: 2,
                marginLeft: 4,
                marginRight: 4,
                ...css({
                    'borderRadius': 2,
                    'backgroundColor': 'inputBackgroundSelected',
                    '&:hover': {
                        backgroundColor: 'inputOutlineHover'
                    }
                })
            }),
            multiValueLabel: (
                styles: CSSObject,
                { isDisabled }: { isDisabled: boolean }
            ) => ({
                ...omit(styles, 'paddingLeft'),
                paddingTop: 5.5,
                paddingBottom: 5.5,
                paddingLeft: 10,
                paddingRight: isDisabled ? 10 : 6,
                ...css({
                    borderRadius: 2,
                    fontSize: 4
                })
            }),
            multiValueRemove: (styles: CSSObject) => ({
                ...styles,
                paddingRight: 8,
                paddingTop: 1,
                ...css({
                    'borderRadius': 2,
                    'color': 'text',
                    '&:hover': {
                        backgroundColor: 'inherit'
                    }
                })
            })
        }),
        [theme, borderColor]
    )

    return styles
}

const DropdownIndicator = () => {
    return (
        <svg
            width="56"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ cursor: 'pointer' }}
        >
            <path
                d="M8.70624 11.4139L11.2962 14.0039C11.6862 14.3939 12.3162 14.3939 12.7062 14.0039L15.2962 11.4139C15.9262 10.7839 15.4762 9.70386 14.5862 9.70386H9.40624C8.51624 9.70386 8.07624 10.7839 8.70624 11.4139Z"
                fill="#BE9191"
            />
        </svg>
    )
}

export const TiaReactSelect = <TOption,>(
    props: (SelectProps<TOption> | AsyncSelectProps<TOption>) & {
        loadingOptions?: boolean
        sx?: ThemeStyleObject
        component?: ReactSelect | AsyncReactSelect
    }
) => {
    const { components: innerComponents, sx, ...rest } = props

    const { theme } = useTiaUI()
    const selectStyles = useSelectStyles(theme)
    const Component = props.component ?? ReactSelect

    return (
        <Box
            sx={{
                ...sx,
                'div:nth-child(2) > svg': {
                    width: '20px',
                    height: '20px',
                    mr: 3
                }
            }}
        >
            <Component<TOption>
                styles={{
                    ...selectStyles
                }}
                components={{
                    // No indicators if component is disabled
                    IndicatorsContainer: props.isDisabled
                        ? () => null
                        : components.IndicatorsContainer,
                    DropdownIndicator: () => <DropdownIndicator />,
                    IndicatorSeparator: () => null,
                    // @ts-expect-error TS is not happy but this follows docs
                    MultiValueRemove: props.isDisabled
                        ? () => null
                        : MultiValueRemove,
                    // @ts-expect-error TS is not happy but this works
                    ClearIndicator,
                    ...innerComponents
                }}
                {...rest}
            />
        </Box>
    )
}

const Control = ({ children, ...props }: ControlProps) => {
    return (
        <components.Control {...props}>
            <components.DropdownIndicator {...props}>
                <MagnifyingGlassIcon />
            </components.DropdownIndicator>
            {children}
        </components.Control>
    )
}

export const SelectWithMagnifyingGlass = <TOption,>(
    props: (SelectProps<TOption> | AsyncSelectProps<TOption>) & {
        loadingOptions?: boolean
        sx?: ThemeStyleObject
        component?: ReactSelect | AsyncReactSelect
    }
) => {
    const { components: innerComponents, sx, ...rest } = props

    const { theme } = useTiaUI()
    const selectStyles = useSelectStyles(theme)
    const Component = props.component ?? ReactSelect

    return (
        <Box
            sx={{
                ...sx,
                'div:nth-child(2) > svg': {
                    width: '20px',
                    height: '20px',
                    mr: 3
                }
            }}
        >
            <Component<TOption>
                styles={{
                    ...selectStyles,
                    valueContainer: (styles: CSSObject) => ({
                        ...styles,
                        minHeight: 46,
                        padding: '5px 0px'
                    })
                }}
                components={{
                    // No indicators if component is disabled
                    IndicatorsContainer: props.isDisabled
                        ? () => null
                        : components.IndicatorsContainer,
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                    // @ts-expect-error TS is not happy but this follows docs
                    MultiValueRemove: props.isDisabled
                        ? () => null
                        : MultiValueRemove,
                    // @ts-expect-error TS is not happy but this works
                    ClearIndicator,
                    Control,
                    ...innerComponents
                }}
                {...rest}
            />
        </Box>
    )
}

export const Select = <TOption = unknown,>(props: SelectProps<TOption>) => {
    const {
        components: innerComponents,
        name,
        validations,
        loadingOptions,
        sx,
        isClearable,
        isMulti,
        ...rest
    } = props
    const { theme } = useTiaUI()
    const { error, loading, control, borderColor } = useTiaInput(props)
    const styles = useSelectStyles(theme, borderColor)
    // by default isMulti isn't clearable (by figma designs) and single select is (we need to be able to clear the value)
    const defaultIsClearable = isClearable || !isMulti

    if (loading) {
        return <Whisper sx={{ mb: 5, height: 7, ...props.sx }} />
    }

    // type copied from VSCode typehint
    const render = (props: {
        onChange?: (...event: any[]) => void
        onBlur?: () => void
        value?: any
        name: string
        ref?: React.MutableRefObject<any>
    }) => (
        <TiaReactSelect<TOption>
            {...rest}
            // props is { value, onBlur, onChange } when <Controller> is rendered (react-hook-form context is present)
            // or { name } when no form context present
            {...props}
            isClearable={defaultIsClearable}
            loadingOptions={loadingOptions}
            isMulti={isMulti}
            styles={styles}
            onChange={(val: TOption, actionMeta: any) => {
                rest.onChange?.(val, actionMeta)
                props.onChange?.(val, actionMeta)
            }}
        />
    )

    return (
        <Box sx={sx}>
            {control ? (
                <Controller
                    name={name}
                    control={control}
                    rules={validations}
                    defaultValue={props.defaultValue}
                    render={({ ...newProps }) => render(newProps)}
                />
            ) : (
                render({ name })
            )}
            {error}
        </Box>
    )
}

export const AsyncSelect = <TOption = unknown,>(
    props: AsyncSelectProps<TOption>
) => {
    const {
        components: innerComponents,
        name,
        validations,
        sx,
        isClearable,
        isMulti,
        ...rest
    } = props
    const { theme } = useTiaUI()
    const { error, loading, control, borderColor } = useTiaInput(props)
    const styles = useSelectStyles(theme, borderColor)
    // by default isMulti isn't clearable (by figma designs) and single select is (we need to be able to clear the value)
    const defaultIsClearable = isClearable || !isMulti

    if (loading) {
        return <Whisper sx={{ mb: 5, height: 7, ...props.sx }} />
    }

    // type copied from VSCode typehint
    const render = (props: {
        onChange?: (...event: any[]) => void
        onBlur?: () => void
        value?: any
        name: string
        ref?: React.MutableRefObject<any>
    }) => (
        <TiaReactSelect<TOption>
            {...rest}
            // props is { value, onBlur, onChange } when <Controller> is rendered (react-hook-form context is present)
            // or { name } when no form context present
            {...props}
            isClearable={defaultIsClearable}
            component={AsyncReactSelect}
            isMulti={isMulti}
            styles={styles}
            onChange={(val: TOption, actionMeta: any) => {
                rest.onChange?.(val, actionMeta)
                props.onChange?.(val, actionMeta)
            }}
        />
    )

    return (
        <Box sx={sx}>
            {control ? (
                <Controller
                    name={name}
                    control={control}
                    rules={validations}
                    defaultValue={props.defaultValue}
                    render={({ ...newProps }) => render(newProps)}
                />
            ) : (
                render({ name })
            )}
            {error}
        </Box>
    )
}
